import type { TableColumn, TableColumns } from '@ohpen/enso-ui';

import { getColumnDecimals } from '@pmp/utils';

import type { CloneField, CloneOperation } from './cloneActions';

// Which columns of a clone table the assistant may carry a change into, read off the table's own column
// config so what it is allowed to change is what the user will be looking at.
//
// A column is described by the field its rows hold the value under, not by its column key: a list shows the
// same field twice (the stored key and the label beside it), and both are the one cell in the clone.

const CRUD_MANAGED_KEYS = ['actions', 'originalCode', 'replacesCode', 'replacedByCode'];

/** The columns a clone table actually shows, mirroring the filter CrudTable applies to them. */
const inCrud = <T,>(column: TableColumn<T>): boolean =>
  typeof column.dataIndex === 'string' &&
  column.options?.hideInForm !== true &&
  column.hideInCrud !== true &&
  !CRUD_MANAGED_KEYS.includes(String(column.key));

// A column the crud renders read-only can be shown but not typed into, and the ones the crud fills in itself
// are nobody else's to set: both are described rather than dropped, so a proposal naming one is refused for
// the reason it exists instead of being reported as a column the list does not have.
const describe = <T,>(column: TableColumn<T>): CloneField => {
  const { format, bulkAdjust, readOnly } = column.options ?? {};

  return {
    key: column.dataIndex as string,
    editable: readOnly !== true,
    decimalScale: getColumnDecimals(format),
    operations: bulkAdjust ? (bulkAdjust.allowedOperations as CloneOperation[]) : undefined,
    maxInput: bulkAdjust?.maxInput,
    allowNegativeInput: bulkAdjust?.allowNegativeInput,
    resultMin: bulkAdjust?.resultMin,
  };
};

// Two columns on the same field are one cell, so their descriptions are folded: the cell can only be typed
// into if every column on it says so, and the numeric detail comes from whichever column carries it.
const fold = (into: CloneField, next: CloneField): CloneField => ({
  key: into.key,
  editable: into.editable && next.editable,
  decimalScale: into.decimalScale ?? next.decimalScale,
  operations: into.operations ?? next.operations,
  maxInput: into.maxInput ?? next.maxInput,
  allowNegativeInput: into.allowNegativeInput ?? next.allowNegativeInput,
  resultMin: into.resultMin ?? next.resultMin,
});

/** Every field of a clone table, keyed as its rows hold it, with what may be done to it. */
export const cloneTableFields = <T,>(columns: TableColumns<T>): CloneField[] => {
  const byField = new Map<string, CloneField>();

  columns.filter(inCrud).forEach(column => {
    const field = describe(column);
    const known = byField.get(field.key);
    byField.set(field.key, known === undefined ? field : fold(known, field));
  });

  return [...byField.values()];
};
